<template>
    <RedesignBaseForm
        ref="editWidgetForm"
        :model="localModel"
        :show-message="false"
        class="widget-external-edit-form"
    >
        <BaseCollapse v-model="activeCollapse">
            <RedesignFormItem
                :label="$t('widget.title')"
                :rules="[required]"
                prop="Title"
            >
                <RedesignBaseInput v-model="localModel.Title" />
            </RedesignFormItem>
            <RedesignFormItem
                v-if="'DefaultRefreshInterval' in localModel"
                :label="$t('widget.widgetRefreshInterval')"
                :info-text="$t('widget.setIntervalOnWidgetReload')"
            >
                <RedesignWidgetRefreshInterval
                    v-model="widgetLayoutModel.DefaultRefreshInterval"
                    :min-value="minIntervalValue"
                    :max-value="maxIntervalValue"
                />
            </RedesignFormItem>
            <RedesignFormItem
                :label="$t('widget.widgetExternalEndpoint')"
            >
                <RedesignBaseInput
                    v-model="widgetLayoutModel.Endpoint"
                    type="textarea"
                    :autosize="{ minRows: 1, maxRows: 4}"
                />
            </RedesignFormItem>
            <RedesignFormItem
                :label="$t('widget.widgetType')"
            >
                <RedesignBaseSelect
                    v-model="widgetLayoutModel.ComponentTypeID"
                    placeholder="Select"
                    :data="componentTypeOptions"
                    :multiple="false"
                    :config="{
                        labelKey: 'label',
                        valueKey: 'value'
                    }"
                    full-width
                />
            </RedesignFormItem>
            <div class="mb-5">
                <BaseCollapseItem
                    v-if="widgetLayoutModel.ComponentTypeID "
                    :title="$t('widget.dictionary')"
                    name="dictionary"
                    class="simple-collapse"
                >
                    <div class="px-1">
                        <json-viewer
                            :value="jsonViewerData"
                            :expand-depth="5"
                            copyable
                            boxed
                            sort
                        />
                    </div>
                </BaseCollapseItem>
            </div>
            <div class="mb-5">
                <BaseCollapseItem
                    :title="$t('widget.layout')"
                    name="layout"
                    class="simple-collapse"
                >
                    <RedesignFormItem
                        :label="$t('widget.widgetPadding')"
                    >
                        <RedesignInputNumber
                            v-model="widgetLayoutModel.padding"
                            :step="1"
                            type="number"
                            :min="0"
                            placeholder="0"
                        />
                    </RedesignFormItem>
                    <RedesignWidgetColors
                        v-model="colorsModel"
                        :available-colors="availableColors"
                    />
                </BaseCollapseItem>
            </div>
        </BaseCollapse>
    </RedesignBaseForm>
</template>

<script setup lang="ts">
import RedesignBaseForm from '@/components/RedesignBaseComponents/Form/RedesignBaseForm.vue'
import { computed, ref } from 'vue'
import { widgetModelData } from '@/composables/useWidgetEditModal'
import BaseCollapse from '@/components/RedesignBaseComponents/Collapse/BaseCollapse.vue'
import RedesignFormItem from '@/components/RedesignBaseComponents/Form/RedesignFormItem.vue'
import RedesignBaseInput from '@/components/RedesignBaseComponents/Inputs/RedesignBaseInput.vue'
import useValidationRules from '@/composables/useValidationRules'
import RedesignWidgetRefreshInterval
    from '@/components/RedesignComponents/Widgets/Common/RedesignWidgetRefreshInterval.vue'
import get from 'lodash/get'
import RedesignBaseSelect from '@/components/RedesignBaseComponents/Inputs/RedesignBaseSelect.vue'
import { dictionary, options } from '@/enum/externalDataWidgetConfig'
import BaseCollapseItem from '@/components/RedesignBaseComponents/Collapse/BaseCollapseItem.vue'
import JsonViewer from 'vue-json-viewer'
import RedesignInputNumber from '@/components/RedesignBaseComponents/Inputs/RedesignInputNumber.vue'
import RedesignWidgetColors from '@/components/RedesignComponents/Widgets/Common/RedesignWidgetColors.vue'
import { WidgetExtended } from '@/types/dashboard'
import { getWidgetLayoutDefaultColors } from '@/components/RedesignComponents/Widgets/widget.helper'

const { required } = useValidationRules()

const editWidgetForm = ref<typeof RedesignBaseForm>()

/* Props */
const props = defineProps<{
    value: WidgetExtended
}>()
/* Emits */
const emit = defineEmits([ 'update:value', 'input' ])

/* Form Model */
const localModel = computed({
    get () {
        return props.value
    },
    set (val) {
        emit('update:value', val)
        emit('input', val)
    }
})
const colorsModel = computed({
    get () {
        return widgetLayoutModel.value.colors
    },
    set (val) {
        widgetLayoutModel.value.colors = { ...val }
    }
})
const widgetLayoutModel = computed({
    get () {
        return localModel.value.WidgetLayout
    },
    set (val) {
        localModel.value.WidgetLayout = { ...val }
    }
})

/* Data */
const activeCollapse = ref([ '' ])

/* Computed */
const templateSettings = computed(() => {
    return get(widgetModelData.value, 'WidgetLayout.TemplateSettings', {
        MinimumRefreshInterval: 30,
        MaximumRefreshInterval: 1800,
    })
})
const minIntervalValue = computed(() => {
    return Number(templateSettings.value.MinimumRefreshInterval)
})
const maxIntervalValue = computed(() => {
    return Number(templateSettings.value.MaximumRefreshInterval)
})
const componentTypeOptions = computed(() => {
    return options
})
const jsonViewerData = computed(() => {
    if (!localModel.value?.WidgetLayout?.ComponentTypeID) {
        return ''
    }
    return dictionary[localModel.value.WidgetLayout.ComponentTypeID] || ''
})
const availableColors = computed(() => {
    return getWidgetLayoutDefaultColors(props.value) || {}
})

/* Methods */
async function validate () {
    const res = await editWidgetForm.value?.validate()
    return res?.isValid
}

defineExpose({
    validate
})
</script>

<style lang="scss">
.widget-external-edit-form {
    .el-checkbox, .el-checkbox__input {
        white-space: normal;
    }
}
</style>
